/**
 * draft-store.ts — Phase 049 Plan 02 (D-049-01 / D-049-02 / D-049-03).
 *
 * Standalone Zustand store carrying the Preview-only DRAFT state — the
 * nested per-block property drafts map, plus the eagerly-computed dirty
 * selectors. Read by `useProjectedApp()` (the LoadedApp projector, Task
 * 6), `DirtyRingOverlay` + `DirtyCountChip` + `DiscardAllButton` (the
 * dirty surfaces, Plan 049-03), the Esc-keydown branch in
 * `usePreviewSelection` (Plan 049-03), and Phase 051's Save controller
 * (READ-ONLY for 049).
 *
 * Why a STANDALONE `create()` store (NOT a slice composed into
 * `src/store/studio-store.ts`):
 *   - The studio store runs through the `persist` middleware with a
 *     `UI_PERSIST_KEYS` allow-list; drafts MUST NEVER persist to
 *     `localStorage` (page reload = natural discard, D-049-02). The
 *     closest analog is `preview-selection-slice.ts` — standalone
 *     `create()` under `features/preview/store/` with no middleware;
 *     this file matches its shape byte-for-byte.
 *   - RESEARCH §"Anti-Patterns": composing into studio-store forces a
 *     `UI_PERSIST_KEYS` edit that would silently exclude drafts — and a
 *     future hand who forgets the allow-list invariant would accidentally
 *     persist them. Standalone store sidesteps the trap.
 *
 * Shape (D-049-01 — nested-per-blockId map; matches Phase 051 whole-block
 * re-emit on Save):
 *
 *   drafts[blockId][propertyKey] = DraftValue
 *
 * `blockId` is the persisted ABox subject IRI (`env.id`) carried verbatim
 * in `PreviewSelectionRef.blockId` from Phase 048. The inner key is the
 * `__aboxEnvelope.properties` key (e.g. `text`, `placeholder`,
 * `iterate_over`).
 *
 * Dirty comparator (D-049-03 — SC#3 invariant): the `dirtyBlockIds`,
 * `dirtyCount`, and `isDirty` selectors do an EAGER draft-vs-baseline
 * deep compare, NOT a "touched-key" tracking heuristic. Type-then-revert
 * (the operator types a value and then types back to baseline) MUST yield
 * `dirtyCount === 0` — false-dirty is impossible by construction
 * (RESEARCH §"Pattern 5" + RESEARCH Pitfall 4).
 *
 * Token-ref preservation ([ASSUMED] A1, RESEARCH §"Pitfall 6"): the
 * comparator delegates to an `isTokenBoundShape(value)` guard that
 * returns `false` for every Phase 049 fixture today (no `tokenRef` shape
 * exists yet — Phase 050's EDIT-02 introduces it). The guard is
 * structurally ready for Phase 050 to flip on — when both draft and
 * baseline carry a `{ tokenRef: string }` shape, the comparator compares
 * by `tokenRef` only (preserving the binding identity). The A1 marker
 * carries forward in `049-02-SUMMARY.md` and `049-SUMMARY.md` until
 * Phase 050 lands EDIT-02.
 */

import { create } from 'zustand'
import { useStudioStore } from '@/store/studio-store'
import { readEnvelope } from '@/features/design-system/ontology/abox-render-prep'

/**
 * Draft value union — left open (`unknown`) in v2.8 per CONTEXT D-049-02
 * Claude's Discretion ("planner picks the union; v2.8 leaves `unknown`").
 * Phase 050 EDIT-02 narrows the union to include token-ref shapes; the
 * comparator's `isTokenBoundShape` guard is already wired for that case.
 */
export type DraftValue = unknown

/** Standalone Zustand state shape — D-049-01 nested-per-blockId map. */
export interface DraftStoreState {
  /**
   * Nested per-block draft map. Outer key `blockId` = persisted ABox
   * subject IRI (`env.id`); inner key = `__aboxEnvelope.properties` key
   * (e.g. `text`, `placeholder`, `iterate_over`). Empty `{}` on fresh
   * module load — no middleware persisting to storage (D-049-02).
   */
  drafts: Record<string, Record<string, DraftValue>>

  /** Write a draft cell. Creates the per-block entry on first write. */
  setDraft: (
    blockId: string,
    propertyKey: string,
    value: DraftValue,
  ) => void

  /**
   * DELETE the block's draft entry (D-049-02 Claude's Discretion
   * recommendation: delete-vs-collapse — deletion makes `dirtyBlockIds`
   * derivation cheaper because we never have to scan an empty `{}` map).
   */
  clearBlock: (blockId: string) => void

  /** Reset to initial state — `drafts = {}`. */
  clearAll: () => void

  /**
   * Phase 051.1 D-051.1-04 (Codex P2 fix) — Read a block's current draft
   * snapshot. The Save controller calls this BEFORE `addBlock` (snapshot)
   * and AGAIN AFTER `addBlock` resolves (post-await read). If the two
   * snapshots deep-equal, the controller clears the draft (normal cleanup
   * path); otherwise a mid-flight edit was detected and the draft is
   * preserved for the operator's next re-save. Returns the per-block
   * draft map (Record<string, unknown>) or `undefined` when no draft
   * exists for that blockId.
   */
  readBlockDraft: (blockId: string) => Record<string, DraftValue> | undefined

  /**
   * D-049-03 — block ids with at least one truly-different draft entry
   * vs baseline. Eager deep compare so type-then-revert never produces
   * a stuck dirty state (SC#3 invariant).
   */
  dirtyBlockIds: () => ReadonlySet<string>

  /**
   * D-049-03 — count of (blockId, propertyKey) pairs that differ from
   * baseline. SC#3 invariant: type-then-revert → 0.
   */
  dirtyCount: () => number

  /**
   * D-049-03 — per-cell verdict. Returns `false` when the cell has no
   * draft entry, or when the draft value deep-equals baseline.
   */
  isDirty: (blockId: string, propertyKey: string) => boolean
}

const initialState = {
  drafts: {} as Record<string, Record<string, DraftValue>>,
}

/**
 * Structural guard for the Phase 050 EDIT-02 token-bound value shape.
 *
 * [ASSUMED] A1 (RESEARCH §"Pitfall 6"): returns `false` for every Phase
 * 049 fixture today — no `tokenRef` shape exists yet. Structurally ready
 * for Phase 050 to flip on (when properties carry
 * `{ tokenRef: 'color.primary' }` instead of bare literals).
 */
function isTokenBoundShape(
  value: unknown,
): value is { tokenRef: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'tokenRef' in value &&
    typeof (value as Record<string, unknown>).tokenRef === 'string'
  )
}

/**
 * Pure JSON deep equality — `__aboxEnvelope.properties` shape is pure
 * JSON (verified at abox-render-prep.ts:81-87 — `Record<string,
 * unknown>` populated by `jsonRow` decoded values; no Date/Map/Symbol/
 * circular refs). `JSON.stringify(a) === JSON.stringify(b)` is O(n) and
 * correct under this constraint (RESEARCH §"Don't Hand-Roll").
 */
function deepEqualJson(a: unknown, b: unknown): boolean {
  // Identity short-circuit covers primitives + same-reference objects.
  if (a === b) return true
  // Reject when one is null/undefined and the other is not — `null`
  // serializes to `"null"`, so JSON-equality alone would conflate.
  if (a == null || b == null) return a === b
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    // Fallback — JSON.stringify can throw on circular references.
    // The envelope properties shape is pure JSON so this branch is
    // structurally unreachable, but be defensive.
    return false
  }
}

/**
 * Comparator — token-ref preservation + deep JSON fallback.
 *
 * D-049-03 + RESEARCH Pitfall 6 + [ASSUMED] A1:
 *   - If EITHER value is a token-bound shape, compare by `tokenRef`
 *     string only. This preserves the binding identity through
 *     property panel re-renders that might restructure other fields.
 *   - Otherwise, deep-equal the values as JSON.
 */
function isDifferent(draftValue: unknown, baselineValue: unknown): boolean {
  if (isTokenBoundShape(draftValue) || isTokenBoundShape(baselineValue)) {
    const dRef = isTokenBoundShape(draftValue) ? draftValue.tokenRef : null
    const bRef = isTokenBoundShape(baselineValue)
      ? baselineValue.tokenRef
      : null
    return dRef !== bRef
  }
  return !deepEqualJson(draftValue, baselineValue)
}

/**
 * Read the baseline value for a `(blockId, propertyKey)` pair off
 * `useStudioStore.getState().currentProject`. Walks every loaded block,
 * finds the one whose envelope `id` matches `blockId`, and returns
 * `env.properties[propertyKey]`. Returns `undefined` for any cell that
 * has no persisted baseline (Phase 050 may write drafts on keys not in
 * the original envelope; the diff treats those as `undefined` baseline).
 *
 * Imperative read pattern (`useStudioStore.getState()`) — established
 * Phase 048 idiom (`use-preview-selection.ts:70,76,97,105,113` +
 * `preview-selection-overlay.tsx:126,209`).
 */
function readBaseline(blockId: string, propertyKey: string): unknown {
  const currentProject = useStudioStore.getState().currentProject
  if (!currentProject?.blocks) return undefined
  for (const block of Object.values(currentProject.blocks)) {
    const env = readEnvelope(block)
    if (env && env.id === blockId) {
      return env.properties[propertyKey]
    }
  }
  return undefined
}

export const useDraftStore = create<DraftStoreState>()((set, get) => ({
  ...initialState,

  setDraft: (blockId, propertyKey, value) =>
    set((s) => ({
      drafts: {
        ...s.drafts,
        [blockId]: {
          ...(s.drafts[blockId] ?? {}),
          [propertyKey]: value,
        },
      },
    })),

  clearBlock: (blockId) =>
    set((s) => {
      // D-049-02 Claude's Discretion: delete, not collapse to `{}` —
      // makes `dirtyBlockIds` derivation cheaper (no empty-map scan).
      const next = { ...s.drafts }
      delete next[blockId]
      return { drafts: next }
    }),

  clearAll: () => set({ ...initialState }),

  // Phase 051.1 D-051.1-04 — Codex P2 mid-flight snapshot read.
  readBlockDraft: (blockId) => get().drafts[blockId],

  // D-049-03 selectors — eager deep compare against baseline.
  // SC#3 invariant: type-then-revert → 0 (false-dirty impossible).

  dirtyBlockIds: () => {
    const result = new Set<string>()
    const drafts = get().drafts
    for (const [blockId, blockDrafts] of Object.entries(drafts)) {
      for (const [key, draftValue] of Object.entries(blockDrafts)) {
        const baseline = readBaseline(blockId, key)
        if (isDifferent(draftValue, baseline)) {
          result.add(blockId)
          break // one dirty cell is sufficient — short-circuit
        }
      }
    }
    return result
  },

  dirtyCount: () => {
    let count = 0
    const drafts = get().drafts
    for (const [blockId, blockDrafts] of Object.entries(drafts)) {
      for (const [key, draftValue] of Object.entries(blockDrafts)) {
        const baseline = readBaseline(blockId, key)
        if (isDifferent(draftValue, baseline)) {
          count += 1
        }
      }
    }
    return count
  },

  isDirty: (blockId, propertyKey) => {
    const drafts = get().drafts
    const blockDrafts = drafts[blockId]
    if (!blockDrafts || !(propertyKey in blockDrafts)) return false
    const draftValue = blockDrafts[propertyKey]
    const baseline = readBaseline(blockId, propertyKey)
    return isDifferent(draftValue, baseline)
  },
}))
